/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo } from "react";

import { isEmpty, isEqual } from "lodash";
import { useLocation } from "react-router-dom";
import {
  type QueryParamConfig,
  BooleanParam,
  createEnumParam,
  decodeJson,
  encodeJson,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { useUser } from "@components/user-context";
import { type TopLevelBreakdown } from "@features/sales-v3/types";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import { logEvent } from "@internal/analytics";
import {
  type CreativesMetricsFilterInput,
  type CreativesSortingInput,
  type CreativesStatusEnum,
  type CreativesTypeEnum,
  CreativeMetricType,
  CreativePlatformEnum,
  CreativesSortingOrder,
} from "@north-beam/nb-client-side";
import {
  type DateRangeChoiceV2,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  CustomJsonCompressedParam,
  MCAttributionWindowDays,
  todayLocalAsISODate,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import {
  type AdColouring,
  allAdColours,
  useAvailableAdColoursAtom,
  useAvailableMetricsByIdAtom,
  useCurrentNumberOfAdsAtom,
} from "../atoms";

const AttributionWindowParam = createEnumParam<IMCAttributionWindowDays>(
  MCAttributionWindowDays.keyOrder,
);

const CustomJsonParam = <T>(): QueryParamConfig<T> => ({
  encode: (value) => encodeJson(value),
  decode: (value) => decodeJson(value),
  equals: (a, b) => isEqual(a, b),
});
type DateRangeType<T> = T | FixedDateInterval;
const DateRangeParam = CustomJsonParam<DateRangeType<DateRangeChoiceV2>>();
const StringArrayParam = CustomJsonParam<string[]>();
const ActiveStatusParam = CustomJsonParam<CreativesStatusEnum | null>();
const CreativeTypeParam = CustomJsonParam<CreativesTypeEnum | null>();

type SelectedAd = {
  adKey: string;
  adColouring: AdColouring;
  adVisualId: string;
};
export type SelectedAdsByAdKey = { [adKey: string]: SelectedAd };
const AdsParam = CustomJsonParam<SelectedAdsByAdKey>();
const MetricsFilterParam = CustomJsonParam<CreativesMetricsFilterInput>();
const BreakdownFilterParam = CustomJsonCompressedParam<TopLevelBreakdown[]>();

enum ReportBody {
  ATTRIBUTION_MODEL = "attributionModel",
  ATTRIBUTION_WINDOW = "attributionWindow",
  GROUP_BY_AD_NAME = "groupByAdName",
  DATE_RANGE = "dateRange",
  PLATFORMS = "platforms",
  HERO_METRIC = "heroMetric",
  SELECTED_SAVED_VIEW_ID = "selectedSaveViewId",
  METRICS = "metrics",
  SORTING = "sorting",
  SORTING_ORDER = "sortingOrder",
  ACTIVE_STATUS = "activeStatus",
  SEARCH_TEXT = "searchText",
  CREATIVE_TYPE = "creativeType",
  SELECTED_ADS = "adKeys",
  METRIC_FILTERS = "metricFilters",
  BREAKDOWN_FILTERS = "breakdownFilters",
}

export type CreativeSorting = Omit<CreativesSortingInput, "order"> & {
  id: string;
};

export interface ReportBodyState {
  readonly attributionModel: string;
  readonly attributionWindow: IMCAttributionWindowDays;
  readonly groupAdsByName: boolean;
  readonly dateRange: DateRangeType<DateRangeChoiceV2>;
  readonly platforms: CreativePlatformEnum[];
  readonly metrics: Set<string>;
  readonly sorting: CreativeSorting;
  readonly sortingOrder: CreativesSortingOrder;
}
export const CreativePlatformArrayParam =
  CustomJsonParam<CreativePlatformEnum[]>();

export const CreativeSortingOrderParam =
  CustomJsonParam<CreativesSortingOrder>();

export const CreativeSortingParam = CustomJsonParam<CreativeSorting>();
export function generateHeroMetricId(
  metric: string,
  attributionModel: string,
  attributionWindow: string,
) {
  return `${metric}__${attributionModel}__${attributionWindow}____accrual`;
}
export const useReportBodyState = () => {
  const { user } = useUser();
  const [availableMetricsById] = useAvailableMetricsByIdAtom();
  const {
    attributionModel: userAttributionModel,
    attributionWindow: userAttributionWindow,
  } = useUserAttributionSettings();

  const location = useLocation();
  const [, setAvailableColours] = useAvailableAdColoursAtom();
  const [, setAdCount] = useCurrentNumberOfAdsAtom();
  const [state, setState] = useQueryParams(
    {
      [ReportBody.ATTRIBUTION_MODEL]: useMemo(
        () =>
          withDefault(
            StringParam,
            user.featureFlags.enableDashboardV2
              ? userAttributionModel
              : user.getDefaultAttributionModel(""),
          ),
        [user, userAttributionModel],
      ),
      [ReportBody.ATTRIBUTION_WINDOW]: useMemo(
        () =>
          withDefault(
            AttributionWindowParam,
            user.featureFlags.enableDashboardV2
              ? userAttributionWindow
              : ("1" as const),
          ),
        [userAttributionWindow],
      ),

      [ReportBody.DATE_RANGE]: useMemo(
        () => withDefault(DateRangeParam, "last7Days" as const),
        [],
      ),

      [ReportBody.GROUP_BY_AD_NAME]: useMemo(
        () => withDefault(BooleanParam, false),
        [],
      ),

      [ReportBody.PLATFORMS]: useMemo(
        () =>
          withDefault(CreativePlatformArrayParam, [
            CreativePlatformEnum.facebook,
            CreativePlatformEnum.snapchat,
            CreativePlatformEnum.tiktok,
            CreativePlatformEnum.applovin,
          ]),
        [],
      ),

      [ReportBody.HERO_METRIC]: useMemo(
        () => withDefault(StringParam, "ROAS"),
        [],
      ),

      [ReportBody.SELECTED_SAVED_VIEW_ID]: useMemo(
        () => withDefault(StringParam, null),
        [],
      ),

      [ReportBody.METRICS]: useMemo(
        () => withDefault(StringArrayParam, []),
        [],
      ),
      [ReportBody.SORTING]: useMemo(
        () =>
          withDefault(CreativeSortingParam, {
            customComposed: false,
            metric: { id: "spend", metric: "spend" },
            id: "spend",
            type: CreativeMetricType.base,
          } as CreativeSorting),
        [],
      ),
      [ReportBody.SORTING_ORDER]: useMemo(
        () =>
          withDefault(CreativeSortingOrderParam, CreativesSortingOrder.desc),
        [],
      ),

      [ReportBody.ACTIVE_STATUS]: useMemo(
        () => withDefault(ActiveStatusParam, null),
        [],
      ),

      [ReportBody.SEARCH_TEXT]: useMemo(() => withDefault(StringParam, ""), []),
      [ReportBody.CREATIVE_TYPE]: useMemo(
        () => withDefault(CreativeTypeParam, null),
        [],
      ),
      [ReportBody.SELECTED_ADS]: useMemo(
        () => withDefault(AdsParam, {} as SelectedAdsByAdKey),
        [],
      ),
      [ReportBody.METRIC_FILTERS]: useMemo(
        () =>
          withDefault(MetricsFilterParam, {} as CreativesMetricsFilterInput),
        [],
      ),
      [ReportBody.BREAKDOWN_FILTERS]: useMemo(
        () => withDefault(BreakdownFilterParam, []),
        [],
      ),
    },
    { updateType: "pushIn" },
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const unparsedSelectedAds = searchParams.get(ReportBody.SELECTED_ADS);
    if (!unparsedSelectedAds) {
      return;
    }

    const selectedAds = JSON.parse(unparsedSelectedAds) as SelectedAdsByAdKey;

    if (isEmpty(selectedAds)) {
      return;
    }

    const availableColours = new Set(allAdColours.map((c) => c.id));
    let highestAdCount = -1;
    Object.values(selectedAds).forEach((ad) => {
      const adNumber = Number(ad.adVisualId.replaceAll("Ad ", ""));
      if (highestAdCount < adNumber) {
        highestAdCount = adNumber;
      }
      availableColours.delete(ad.adColouring.id);
    });

    setAdCount(highestAdCount + 1);
    setAvailableColours(allAdColours.filter((c) => availableColours.has(c.id)));
  }, []);

  const resetState = useCallback(() => {
    setAvailableColours(allAdColours);
    setState({ [ReportBody.SELECTED_ADS]: {} });
  }, [setAvailableColours]);

  const dateRange = useMemo(
    () =>
      parseDateRangeArgument(
        state[ReportBody.DATE_RANGE],
        todayLocalAsISODate(),
      ),
    [state[ReportBody.DATE_RANGE]],
  );

  const metricSet = useMemo(() => {
    const usableMetrics = state[ReportBody.METRICS].filter(
      (metric) => availableMetricsById[metric],
    );
    return new Set(usableMetrics);
  }, [state[ReportBody.METRICS], availableMetricsById]);

  const setStateWithReset = useCallback(
    (...args: Parameters<typeof setState>) => {
      setState(...args);
      resetState();
    },
    [setState, resetState],
  );

  const _metricFilters: CreativesMetricsFilterInput | null = useMemo(() => {
    const filters = state[ReportBody.METRIC_FILTERS];
    if (!filters?.metricClauses) {
      return {} as CreativesMetricsFilterInput;
    }
    return {
      ...filters,
      metricClauses: filters.metricClauses.map((clause) => {
        return {
          ...clause,
          metric: {
            ...clause.metric,
            details: {
              ...clause.metric.details,
              accountingMode: "accrual",
              am: state[ReportBody.ATTRIBUTION_MODEL],
              aw: state[ReportBody.ATTRIBUTION_WINDOW],
            },
          },
        };
      }),
    };
  }, [state]);

  const _sorting = useMemo(() => {
    const sort = state[ReportBody.SORTING];
    return {
      ...sort,
      metric: {
        ...sort.metric,
        details: {
          ...sort.metric.details,
          accountingMode: "accrual",
          am: state[ReportBody.ATTRIBUTION_MODEL],
          aw: state[ReportBody.ATTRIBUTION_WINDOW],
        },
      },
    };
  }, [state]);

  return {
    state,
    setState: setStateWithReset,

    selectedAdsByAdKey: state[ReportBody.SELECTED_ADS],
    setSelectedAdsByAdKey: useCallback(
      (adKeys: { [adKey: string]: SelectedAd }) => {
        setState({ [ReportBody.SELECTED_ADS]: adKeys });
      },
      [setState],
    ),

    metricFilters: _metricFilters,
    setMetricFilters: useCallback(
      (metricFilters: CreativesMetricsFilterInput) => {
        resetState();
        setState({ [ReportBody.METRIC_FILTERS]: metricFilters });
      },
      [setState],
    ),

    creativeType: state[ReportBody.CREATIVE_TYPE],
    setCreativeType: useCallback(
      (value: CreativesTypeEnum | null) => {
        resetState();

        setState({ [ReportBody.CREATIVE_TYPE]: value });
      },
      [setState],
    ),

    searchText: state[ReportBody.SEARCH_TEXT],
    setSearchText: useCallback(
      (value: string) => {
        setState({ [ReportBody.SEARCH_TEXT]: value });
      },
      [setState],
    ),

    breakdownFilters: state[ReportBody.BREAKDOWN_FILTERS],
    setBreakdownFilters: useCallback(
      (value: TopLevelBreakdown[]) => {
        setState({ [ReportBody.BREAKDOWN_FILTERS]: value });
      },
      [setState],
    ),

    activeStatus: state[ReportBody.ACTIVE_STATUS],
    setActiveStatus: useCallback(
      (value: CreativesStatusEnum | null) => {
        resetState();

        setState({ [ReportBody.ACTIVE_STATUS]: value });
      },
      [setState],
    ),

    sorting: _sorting,
    setSorting: useCallback(
      (value: CreativeSorting) => {
        resetState();

        setState({ [ReportBody.SORTING]: value });
      },
      [setState],
    ),

    sortingOrder: state[ReportBody.SORTING_ORDER],
    setSortingOrder: useCallback(
      (value: CreativesSortingOrder) => {
        resetState();

        setState({ [ReportBody.SORTING_ORDER]: value });
      },
      [setState],
    ),

    heroMetricId: generateHeroMetricId(
      state[ReportBody.HERO_METRIC],
      state[ReportBody.ATTRIBUTION_MODEL],
      state[ReportBody.ATTRIBUTION_WINDOW],
    ),
    metrics: metricSet,
    setMetrics: useCallback(
      (value: Set<string>) => {
        setState({ [ReportBody.METRICS]: [...value] });
      },
      [setState],
    ),

    selectedSaveViewId: state[ReportBody.SELECTED_SAVED_VIEW_ID],
    setSelectedSaveViewId: useCallback(
      (value: string) => {
        resetState();
        setState({ [ReportBody.SELECTED_SAVED_VIEW_ID]: value });
      },
      [setState],
    ),

    heroMetric: state[ReportBody.HERO_METRIC],
    setHeroMetric: useCallback(
      (value: string) => {
        logEvent("Saved Views: Hero metric changed", {
          from: state[ReportBody.HERO_METRIC],
          to: value,
        });

        setState({ [ReportBody.HERO_METRIC]: value });
      },
      [setState],
    ),

    attributionModel: state[ReportBody.ATTRIBUTION_MODEL],
    setAttributionModel: useCallback(
      (value: string) => {
        logEvent("Saved Views: User changed Attribution Model", {
          from: state[ReportBody.ATTRIBUTION_MODEL],
          to: value,
        });
        resetState();

        setState({
          [ReportBody.ATTRIBUTION_MODEL]: value,
          [ReportBody.SORTING]: {
            ...state[ReportBody.SORTING],
            metric: {
              ...state[ReportBody.SORTING].metric,
              id: state[ReportBody.SORTING].metric.id.replace(
                `__${state[ReportBody.ATTRIBUTION_MODEL]}__`,
                `__${value}__`,
              ),
              details: {
                type: "",
                aw: state[ReportBody.ATTRIBUTION_WINDOW],
                accountingMode: "accrual",
                am: value,
                ...state[ReportBody.SORTING].metric.details,
              },
            },

            id: state[ReportBody.SORTING].id.replace(
              `__${state[ReportBody.ATTRIBUTION_MODEL]}__`,
              `__${value}__`,
            ),
          },
        });
      },
      [
        setState,
        state[ReportBody.SORTING],
        state[ReportBody.ATTRIBUTION_MODEL],
        state[ReportBody.ATTRIBUTION_WINDOW],
      ],
    ),

    attributionWindow: state[ReportBody.ATTRIBUTION_WINDOW],
    setAttributionWindow: useCallback(
      (value: IMCAttributionWindowDays) => {
        logEvent("Saved Views: User changed Attribution Window", {
          from: state[ReportBody.ATTRIBUTION_WINDOW],
          to: value,
        });
        resetState();

        setState({
          [ReportBody.ATTRIBUTION_WINDOW]: value,
          [ReportBody.SORTING]: {
            ...state[ReportBody.SORTING],
            metric: {
              ...state[ReportBody.SORTING].metric,
              id: state[ReportBody.SORTING].metric.id.replace(
                `__${state[ReportBody.ATTRIBUTION_WINDOW]}__`,
                `__${value}__`,
              ),
              details: {
                type: "",
                am: state[ReportBody.ATTRIBUTION_MODEL],
                accountingMode: "accrual",
                aw: value,
                ...state[ReportBody.SORTING].metric.details,
              },
            },

            id: state[ReportBody.SORTING].id.replace(
              `__${state[ReportBody.ATTRIBUTION_WINDOW]}__`,
              `__${value}__`,
            ),
          },
        });
      },
      [
        setState,
        state[ReportBody.SORTING],
        state[ReportBody.ATTRIBUTION_WINDOW],
      ],
    ),

    groupAdsByName: state[ReportBody.GROUP_BY_AD_NAME],
    setGroupAdsByName: useCallback(
      (value: boolean) => {
        logEvent("Saved Views: User changed groupAdsByName", {
          from: state[ReportBody.GROUP_BY_AD_NAME],
          to: value,
        });

        resetState();
        setState({ [ReportBody.GROUP_BY_AD_NAME]: value });
      },
      [setState],
    ),

    selectedPlatforms: state[ReportBody.PLATFORMS],
    setSelectedPlatforms: useCallback(
      (value: CreativePlatformEnum[]) => {
        logEvent("Saved Views: User changed platforms", {
          from: state[ReportBody.PLATFORMS],
          to: value,
        });
        resetState();
        setState({ [ReportBody.PLATFORMS]: value });
      },
      [setState],
    ),

    dateRange,
    unparsedDateRange: state[ReportBody.DATE_RANGE],
    setDateRange: useCallback(
      (value: FixedDateInterval | DateRangeChoiceV2) => {
        logEvent("Saved Views: User changed Date Range", {
          from: state[ReportBody.DATE_RANGE],
          to: dateRange,
        });
        resetState();
        setState({ [ReportBody.DATE_RANGE]: value });
      },
      [setState],
    ),
    resetState,
  };
};
